import React, { useState, useEffect } from "react";
import { db, auth } from "../../firebase";
import { collection, getDocs, query, orderBy, doc } from "firebase/firestore";
import AddTrade from "../../pages/AddTrade";
import { PlusCircle, History, Calendar, TrendingUp, ArrowUpRight, ArrowDownLeft } from "lucide-react";

export default function MobileAddTrade() {
  const [view, setView] = useState("form");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  // FETCH DATA: Uses logic from AddTrade.js to fetch from account sub-collections
  const loadMobileTransactions = async () => {
    if (!auth.currentUser) return;
    setLoading(true);
    try {
      const uid = auth.currentUser.uid;
      const accSnap = await getDocs(query(collection(db, "users", uid, "accounts"), orderBy("name")));
      const accs = accSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      let allRows = [];
      for (const acc of accs) {
        const accRef = doc(db, "users", uid, "accounts", acc.id);
        
        const tradesSnap = await getDocs(collection(accRef, "trades"));
        tradesSnap.forEach(d => {
          const data = d.data();
          allRows.push({
            id: d.id,
            ...data,
            broker: acc.name,
            type: "TRADE",
            amount: data.pnl,
            currency: acc.currency || "₹",
            createdAt: data.createdAt?.toDate?.() || new Date(data.date)
          });
        });

        const wdSnap = await getDocs(collection(accRef, "withdrawals"));
        wdSnap.forEach(d => {
          const data = d.data();
          allRows.push({
            id: d.id,
            ...data,
            broker: acc.name,
            type: "WITHDRAWAL",
            amount: data.amount,
            currency: acc.currency || "₹",
            createdAt: data.createdAt?.toDate?.() || new Date(data.date)
          });
        });
      }

      allRows.sort((a, b) => b.createdAt - a.createdAt);
      setTransactions(allRows);
    } catch (err) {
      console.error("Mobile Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (view === "history") {
      loadMobileTransactions();
    }
  }, [view]);

  return (
    <div style={styles.container}>
      {/* 1. VIEW TOGGLE */}
      <div style={styles.toggleContainer}>
        <button 
          onClick={() => setView("form")} 
          style={{...styles.toggleBtn, background: view === "form" ? "#38bdf8" : "transparent", color: view === "form" ? "#000" : "#64748b"}}
        >
          <PlusCircle size={16} />
          <span>New Entry</span>
        </button>
        <button 
          onClick={() => setView("history")} 
          style={{...styles.toggleBtn, background: view === "history" ? "#38bdf8" : "transparent", color: view === "history" ? "#000" : "#64748b"}}
        >
          <History size={16} />
          <span>Recent</span>
        </button>
      </div>

      {/* 2. THE IRONCLAD CSS OVERRIDE - Targeting the specific overflow causes */}
      <style>{`
        /* 1. FORCE THE WRAPPER TO PREVENT HORIZONTAL SCROLL */
        .mobile-bridge-container {
          width: 100% !important;
          max-width: 100vw !important;
          overflow-x: hidden !important;
          display: block !important;
          box-sizing: border-box !important;
        }

        /* 2. KILL THE 420px WEB WIDTH AND PADDING */
        .mobile-bridge-container [style*="formSection"] { 
          width: 100% !important; 
          min-width: 0 !important;   /* This kills the desktop width overflow */
          max-width: 100% !important;
          flex: none !important; 
          padding: 0 !important;
          margin: 0 !important;
        }

        /* 3. CENTER THE FORM CARD AND FIX EDGES */
        .mobile-bridge-container [style*="formCard"] { 
          width: auto !important; /* Allow margins to work */
          min-width: 0 !important;
          margin: 0 16px 20px 16px !important; /* Perfect 16px gap on both sides */
          padding: 20px 15px !important; 
          box-sizing: border-box !important; /* Ensures padding stays INSIDE the box */
          border-radius: 16px !important;
          background: rgba(13, 17, 23, 0.8) !important;
          border: 1px solid #1e293b !important;
          display: block !important;
        }

        /* 4. STACK THE GRID ROWS */
        .mobile-bridge-container [style*="gridRow"] { 
          display: flex !important;
          flex-direction: column !important; 
          gap: 16px !important;
          width: 100% !important;
        }

        .mobile-bridge-container [style*="gridCol"] {
          width: 100% !important;
          min-width: 0 !important;
        }

        /* 5. FIX ALL INPUTS TO STAY INSIDE CARD */
        .mobile-bridge-container input, 
        .mobile-bridge-container select, 
        .mobile-bridge-container textarea, 
        .mobile-bridge-container [style*="customSelectTrigger"] {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
          font-size: 16px !important; 
        }

        /* 6. CLEAN UP DESKTOP LEFTOVERS */
        .mobile-bridge-container [style*="mainLayout"] { display: block !important; width: 100% !important; }
        .mobile-bridge-container [style*="tableSection"], 
        .mobile-bridge-container [style*="divider"], 
        .mobile-bridge-container h2 { 
          display: none !important; 
        }
      `}</style>

      <div className="mobile-bridge-container">
        {view === "form" ? (
          <AddTrade />
        ) : (
          /* 3. MOBILE-FIRST TRANSACTION LIST */
          <div style={styles.historyList}>
            {loading ? (
              <p style={styles.statusText}>Syncing transactions...</p>
            ) : transactions.length === 0 ? (
              <p style={styles.statusText}>No history found.</p>
            ) : (
              transactions.map(tx => (
                <div key={tx.id} style={styles.card}>
                  <div style={styles.cardTop}>
                    <div>
                      <div style={{
                        ...styles.typeBadge, 
                        color: tx.type === "TRADE" ? "#38bdf8" : "#fbbf24",
                        background: tx.type === "TRADE" ? "rgba(56, 189, 248, 0.1)" : "rgba(251, 191, 36, 0.1)"
                      }}>
                        {tx.type === "TRADE" ? <ArrowUpRight size={10} /> : <ArrowDownLeft size={10} />}
                        {tx.type}
                      </div>
                      <div style={styles.brokerName}>{tx.broker}</div>
                    </div>
                    <div style={{
                      ...styles.amount, 
                      color: tx.amount >= 0 ? "#22c55e" : "#ef4444"
                    }}>
                      {tx.amount < 0 ? "-" : "+"}{tx.currency}{Math.abs(tx.amount).toLocaleString()}
                    </div>
                  </div>
                  
                  <div style={styles.cardBottom}>
                    <div style={styles.metaItem}><Calendar size={12}/> {tx.date}</div>
                    {tx.instrument && <div style={styles.metaItem}><TrendingUp size={12}/> {tx.instrument}</div>}
                    {tx.session && <div style={styles.metaItem}>{tx.session}</div>}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { width: "100%", paddingBottom: "120px", overflowX: "hidden" },
  toggleContainer: { 
    display: "flex", 
    background: "#0f172a", 
    borderRadius: "14px", 
    padding: "4px", 
    margin: "0 20px 20px 20px", 
    border: "1px solid #1e293b" 
  },
  toggleBtn: { 
    flex: 1, 
    display: "flex", 
    alignItems: "center", 
    justifyContent: "center", 
    gap: "8px", 
    border: "none", 
    padding: "12px", 
    borderRadius: "10px", 
    fontSize: "13px", 
    fontWeight: "800", 
    transition: "0.2s ease" 
  },
  historyList: { padding: "0 16px", display: "flex", flexDirection: "column", gap: "12px" },
  card: { 
    background: "rgba(30, 41, 59, 0.4)", 
    border: "1px solid #1e293b", 
    borderRadius: "16px", 
    padding: "16px" 
  },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "14px" },
  typeBadge: { 
    fontSize: "9px", 
    fontWeight: "900", 
    padding: "4px 8px", 
    borderRadius: "6px", 
    display: "flex", 
    alignItems: "center", 
    gap: "4px", 
    marginBottom: "6px"
  },
  brokerName: { color: "#fff", fontWeight: "700", fontSize: "15px" },
  amount: { fontWeight: "900", fontSize: "17px" },
  cardBottom: { display: "flex", gap: "16px", color: "#64748b", alignItems: "center" },
  metaItem: { display: "flex", alignItems: "center", gap: "4px", fontSize: "12px" },
  statusText: { textAlign: "center", color: "#64748b", marginTop: "40px", fontSize: "14px" }
};